import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, UsersRound, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth, useTheme } from '../../global-components/src';
import { getImageUser, getImageUserNotFound } from '../../global-components/src/utils/auth';
import { api } from '../../services/api';
import './style.css';

type SuggestedUser = {
    id: number;
    name: string;
    user_name: string;
    img_account?: string | null;
    verified_profile?: string | null;
    position?: number;
    stamps?: string[] | null;
};

type SuggestionsResponse = {
    users?: SuggestedUser[];
};

const SUGGESTIONS_COOLDOWN_MS = 60 * 60 * 1000;

export default function PeopleYouMayKnow() {
    const { token, isAuthenticated, user } = useAuth();
    const { isBlueTheme } = useTheme();
    const { t } = useTranslation();
    const [users, setUsers] = useState<SuggestedUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [followingId, setFollowingId] = useState<number | null>(null);

    useEffect(() => {
        let active = true;

        const cacheKey = `people-you-may-know:last-visible:${user?.id ?? 'guest'}`;
        const lastVisibleAt = Number(localStorage.getItem(cacheKey));

        if (
            !isAuthenticated ||
            !token ||
            (Number.isFinite(lastVisibleAt) &&
                Date.now() - lastVisibleAt < SUGGESTIONS_COOLDOWN_MS)
        ) {
            setUsers([]);
            setLoading(false);
            return () => {
                active = false;
            };
        }

        setLoading(true);
        api(token)
            .get<SuggestionsResponse>('/users/suggestions')
            .then(response => {
                if (!active) return;
                localStorage.setItem(cacheKey, String(Date.now()));
                setUsers(Array.isArray(response.data.users) ? response.data.users : []);
            })
            .catch(() => {
                if (active) setUsers([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [isAuthenticated, token, user?.id]);

    const follow = async (userId: number) => {
        if (!token || followingId) return;

        setFollowingId(userId);
        try {
            await api(token).post(`/users/follow/${userId}`);
            setUsers(current => current.filter(user => user.id !== userId));
        } catch {
            // The regular profile remains available if a follow action fails.
        } finally {
            setFollowingId(null);
        }
    };

    if (!isAuthenticated || hidden || (!loading && users.length === 0)) return null;

    return (
        <section className={`people-you-may-know ${isBlueTheme ? 'people-you-may-know--blue' : 'people-you-may-know--brown'}`} aria-label={t('suggestions.title', { defaultValue: 'Pessoas que talvez você conheça' })}>
            <header className="people-you-may-know__header">
                <div className="people-you-may-know__heading">
                    <UsersRound size={21} aria-hidden="true" />
                    <h2>{t('suggestions.title', { defaultValue: 'Pessoas que talvez você conheça' })}</h2>
                </div>
                <button
                    aria-label={t('suggestions.close', { defaultValue: 'Fechar sugestões' })}
                    className="people-you-may-know__close"
                    onClick={() => setHidden(true)}
                    type="button"
                >
                    <X size={20} />
                </button>
            </header>

            {loading ? (
                <div className="people-you-may-know__loading" aria-live="polite" />
            ) : (
                <div className="people-you-may-know__list">
                    {users.map(user => (
                        <article className="people-you-may-know__card" key={user.id}>
                            <Link aria-label={user.name} to={`/@${user.user_name}`}>
                                <img
                                    alt=""
                                    className="people-you-may-know__image"
                                    onError={event => {
                                        event.currentTarget.src = getImageUserNotFound(user.name);
                                    }}
                                    src={getImageUser(user.img_account ?? undefined, undefined, user.name)}
                                />
                            </Link>
                            <div className="people-you-may-know__content">
                                <Link className="people-you-may-know__name" to={`/@${user.user_name}`}>
                                    {user.name}
                                </Link>
                                <span className="people-you-may-know__username">@{user.user_name}</span>
                                <button
                                    className="people-you-may-know__follow"
                                    disabled={followingId === user.id}
                                    onClick={() => follow(user.id)}
                                    type="button"
                                >
                                    <UserPlus size={17} />
                                    {followingId === user.id
                                        ? t('suggestions.following', { defaultValue: 'Seguindo…' })
                                        : t('suggestions.follow', { defaultValue: 'Seguir' })}
                                </button>
                            </div>
                            <button
                                aria-label={t('suggestions.remove', { defaultValue: 'Remover sugestão' })}
                                className="people-you-may-know__remove"
                                onClick={() => setUsers(current => current.filter(candidate => candidate.id !== user.id))}
                                type="button"
                            >
                                <X size={17} />
                            </button>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}
